import { z } from "zod";
import { twinLogicActivationEventV1Schema, twinLogicInstallationV1Schema, twinLogicProductVersionV1Schema, } from "./twin-logic.js";
const boundedText = (maximum) => z.string().trim().min(1).max(maximum);
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/u);
const productIdSchema = z.string().regex(/^tl_prod_[0-9a-f]{32}$/u);
const productVersionIdSchema = z.string().regex(/^tl_pver_[0-9a-f]{32}$/u);
const installationIdSchema = z.string().regex(/^tl_inst_[0-9a-f]{32}$/u);
const installationAttemptIdSchema = z.string().regex(/^tl_iatt_[0-9a-f]{32}$/u);
const activationEventIdSchema = z.string().regex(/^tl_act_[0-9a-f]{32}$/u);
export const twinLogicInstallProductVersionInputSchema = z.object({
    idempotencyKey: boundedText(200),
    expectedProductVersionSha256: sha256Schema,
    reason: boundedText(4_000),
}).strict();
export const twinLogicInstallationAttemptV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-logic.installation-attempt.v1"),
    tenantSlug: boundedText(120),
    organisationId: boundedText(200),
    productId: productIdSchema,
    productVersionId: productVersionIdSchema,
    idempotencyKey: boundedText(200),
    requestSha256: sha256Schema,
    reason: boundedText(4_000),
    status: z.enum(["succeeded", "failed"]),
    installationId: installationIdSchema.nullable(),
    errorCode: boundedText(120).nullable(),
    errorMessage: boundedText(4_000).nullable(),
    warehouseReceiptSha256: sha256Schema,
    actor: boundedText(200),
    createdAt: z.number().int().positive().safe(),
}).strict().superRefine((attempt, context) => {
    const success = attempt.status === "succeeded";
    const fieldsMatch = success
        ? attempt.installationId !== null && attempt.errorCode === null &&
            attempt.errorMessage === null
        : attempt.installationId === null && attempt.errorCode !== null &&
            attempt.errorMessage !== null;
    if (!fieldsMatch) {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "Installation attempt status must match its success or failure fields.",
        });
    }
});
export const twinLogicInstallProductVersionResponseSchema = z.object({
    attemptId: installationAttemptIdSchema,
    replayed: z.boolean(),
    attempt: twinLogicInstallationAttemptV1Schema,
    installation: twinLogicInstallationV1Schema.nullable(),
}).strict().superRefine((response, context) => {
    if ((response.attempt.status === "succeeded") !== (response.installation !== null)) {
        context.addIssue({
            code: "custom",
            path: ["installation"],
            message: "A successful attempt must return its immutable installation receipt.",
        });
    }
});
export const twinLogicActivationInputSchema = z.object({
    idempotencyKey: boundedText(200),
    installationId: installationIdSchema,
    expectedPreviousActivationEventId: activationEventIdSchema.nullable(),
    reason: boundedText(4_000),
}).strict();
export const twinLogicActivationResponseSchema = z.object({
    eventId: activationEventIdSchema,
    eventSha256: sha256Schema,
    replayed: z.boolean(),
    event: twinLogicActivationEventV1Schema,
}).strict();
export const twinLogicActivationHeadV1Schema = z.object({
    activationEventId: activationEventIdSchema,
    productVersionId: productVersionIdSchema,
    installationId: installationIdSchema,
    kind: z.enum(["activated", "deactivated"]),
    createdAt: z.number().int().positive().safe(),
}).strict();
export const twinLogicProductInventoryItemV1Schema = z.object({
    productId: productIdSchema,
    productVersionId: productVersionIdSchema,
    productSha256: sha256Schema,
    product: twinLogicProductVersionV1Schema,
    querySemanticsStatus: z.enum(["current", "reauthor_required"]),
    latestInstallationId: installationIdSchema.nullable(),
    activationHead: twinLogicActivationHeadV1Schema.nullable(),
    active: z.object({
        activationEventId: activationEventIdSchema,
        installationId: installationIdSchema,
        activatedAt: z.number().int().positive().safe(),
    }).strict().nullable(),
}).strict();
export const twinLogicProductInventoryResponseSchema = z.object({
    items: z.array(twinLogicProductInventoryItemV1Schema).max(10_000),
}).strict();
